import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TimeRangeEnum } from '../components/FilterSection/TimeRangeSelector/types';
import { DeviceFilterEnum } from '../components/FilterSection/DeviceSelector/types';

/** URL query-param keys used to persist the Analytics filters. */
export const ANALYTICS_FILTER_QUERY_PARAM = {
  timeRange: 'timeRange',
  product: 'product',
  device: 'device',
} as const;

/** Default product selection (mirrors ProductSelector's "All Syte Products" option). */
export const DEFAULT_PRODUCT = 'All Syte Products';

export interface AnalyticsFilters {
  timeRange: TimeRangeEnum;
  product: string;
  device: DeviceFilterEnum;
}

const isTimeRange = (value: string | null): value is TimeRangeEnum =>
  value !== null && (Object.values(TimeRangeEnum) as string[]).includes(value);

const isDevice = (value: string | null): value is DeviceFilterEnum =>
  value !== null && (Object.values(DeviceFilterEnum) as string[]).includes(value);

/**
 * Parse Analytics filters from a URL query string.
 *
 * `timeRange` and `device` are validated against their fixed enums — missing or invalid
 * values fall back to the defaults. `product` is a shop-specific dynamic list loaded at
 * runtime, so any non-empty string is accepted here (only an empty value falls back).
 */
export const parseAnalyticsFilters = (search: string): AnalyticsFilters => {
  const params = new URLSearchParams(search);
  const timeRange = params.get(ANALYTICS_FILTER_QUERY_PARAM.timeRange);
  const product = params.get(ANALYTICS_FILTER_QUERY_PARAM.product);
  const device = params.get(ANALYTICS_FILTER_QUERY_PARAM.device);

  return {
    timeRange: isTimeRange(timeRange) ? timeRange : TimeRangeEnum.Last30Days,
    product: product && product.trim() ? product : DEFAULT_PRODUCT,
    device: isDevice(device) ? device : DeviceFilterEnum.AllDevices,
  };
};

/**
 * Serialize the Analytics filters into a query string, preserving any other params that
 * already exist on the URL.
 */
export const buildAnalyticsFilterSearch = (
  currentSearch: string,
  filters: AnalyticsFilters
): string => {
  const params = new URLSearchParams(currentSearch);
  params.set(ANALYTICS_FILTER_QUERY_PARAM.timeRange, filters.timeRange);
  params.set(ANALYTICS_FILTER_QUERY_PARAM.product, filters.product);
  params.set(ANALYTICS_FILTER_QUERY_PARAM.device, filters.device);
  return params.toString();
};

export interface UseAnalyticsUrlFilters extends AnalyticsFilters {
  setTimeRange: (timeRange: TimeRangeEnum) => void;
  setProduct: (product: string) => void;
  setDevice: (device: DeviceFilterEnum) => void;
}

/**
 * Owns the Analytics filter state and keeps it in sync with the URL query params, so that
 * reloading or sharing a link restores the exact selected view.
 *
 * - Initial state is read from the URL once (validated; invalid → defaults).
 * - Changes are written back with `history.replace`, so filter changes do NOT push new
 *   browser-history entries — Back/Forward keep navigating between pages, not filter states.
 * - Other existing query params are preserved.
 */
export const useAnalyticsUrlFilters = (): UseAnalyticsUrlFilters => {
  const history = useHistory();
  const location = useLocation();

  // Read the initial filters from the URL exactly once, then own the state locally.
  const [initialFilters] = useState<AnalyticsFilters>(() => parseAnalyticsFilters(location.search));
  const [timeRange, setTimeRange] = useState<TimeRangeEnum>(initialFilters.timeRange);
  const [product, setProduct] = useState<string>(initialFilters.product);
  const [device, setDevice] = useState<DeviceFilterEnum>(initialFilters.device);

  useEffect(() => {
    const nextSearch = buildAnalyticsFilterSearch(location.search, {
      timeRange,
      product,
      device,
    });
    // Only touch history when something actually changed (avoids redundant replaces/loops).
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      history.replace({ search: nextSearch });
    }
    // Deliberately keyed only on the filter values: state is the source of truth after
    // mount, so we don't re-sync from `location` on external URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, product, device]);

  return { timeRange, setTimeRange, product, setProduct, device, setDevice };
};
